import html2canvas from "html2canvas"
import { jsPDF } from "jspdf"
import type { HarnessInvoicePdfSizing } from "@/api/providers/invoices"

const LETTER_WIDTH_PX = 816
const READY_TIMEOUT_MS = 10000
const LOGO_LOCK_WIDTH_PX = 140
const LOGO_LOCK_HEIGHT_PX = 56
const FALLBACK_LOGO_PATH = "/assets/cloudfit-logo.png"
const INVOICE_LOGO_SELECTOR = 'img[data-cf-role="invoice-logo"]'

type BuildInvoicePdfArgs = {
  html: string
  fileName: string
  pdfSizing?: HarnessInvoicePdfSizing
}

type ResolvedPdfSizing = {
  letterWidthPx: number
  logoLockWidthPx: number
  logoLockHeightPx: number
}

function resolvePdfSizing(pdfSizing?: HarnessInvoicePdfSizing): ResolvedPdfSizing {
  return {
    letterWidthPx: pdfSizing?.letterWidthPx ?? LETTER_WIDTH_PX,
    logoLockWidthPx: pdfSizing?.logoLockWidthPx ?? LOGO_LOCK_WIDTH_PX,
    logoLockHeightPx: pdfSizing?.logoLockHeightPx ?? LOGO_LOCK_HEIGHT_PX,
  }
}

function isSvgSource(value: string) {
  return /\.svg(?:$|[?#])/i.test(value) || value.startsWith("data:image/svg+xml")
}

function getInvoiceLogoImages(documentRef: Document) {
  return Array.from(documentRef.querySelectorAll<HTMLImageElement>(INVOICE_LOGO_SELECTOR))
}

function applyKnownLogoSourceFallbacks(documentRef: Document) {
  const images = getInvoiceLogoImages(documentRef)
  const replacement = new URL(FALLBACK_LOGO_PATH, window.location.origin).toString()

  images.forEach((image) => {
    const source = image.currentSrc || image.src || ""
    if (!isSvgSource(source) || source === replacement) {
      return
    }

    image.removeAttribute("srcset")
    image.src = replacement
  })
}

function resolvePixelValue(value: string) {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

function resolveImageDeclaredWidth(image: HTMLImageElement) {
  const fromStyleWidth = resolvePixelValue(image.style.width)
  if (fromStyleWidth > 0) {
    return fromStyleWidth
  }

  const fromStyleMaxWidth = resolvePixelValue(image.style.maxWidth)
  if (fromStyleMaxWidth > 0) {
    return fromStyleMaxWidth
  }

  return resolvePixelValue(image.getAttribute("width") ?? "")
}

function resolveImageDeclaredHeight(image: HTMLImageElement) {
  const hasAutoHeight = image.style.height.trim().toLowerCase() === "auto"
  if (!hasAutoHeight) {
    const fromStyleHeight = resolvePixelValue(image.style.height)
    if (fromStyleHeight > 0) {
      return fromStyleHeight
    }
  }

  return resolvePixelValue(image.getAttribute("height") ?? "")
}

function stabilizeSvgImageSizing(documentRef: Document, sizing: ResolvedPdfSizing) {
  const images = Array.from(documentRef.images)
  const logoImages = new Set(getInvoiceLogoImages(documentRef))

  images.forEach((image) => {
    const source = image.currentSrc || image.src || ""
    const shouldLockAsLogo = logoImages.has(image)
    if (!isSvgSource(source) && !shouldLockAsLogo) {
      return
    }

    const computed = documentRef.defaultView?.getComputedStyle(image)
    const rect = image.getBoundingClientRect()
    const parentRect = image.parentElement?.getBoundingClientRect()

    const declaredWidth = resolveImageDeclaredWidth(image)
    const declaredHeight = resolveImageDeclaredHeight(image)
    const parentWidth = parentRect?.width ?? 0

    const width =
      (declaredWidth > 0 && parentWidth > 0 ? Math.min(declaredWidth, parentWidth) : declaredWidth) ||
      rect.width ||
      resolvePixelValue(computed?.width ?? "") ||
      image.width ||
      image.naturalWidth

    let height =
      declaredHeight ||
      rect.height ||
      resolvePixelValue(computed?.height ?? "") ||
      image.height ||
      image.naturalHeight

    if (height <= 0 && width > 0 && image.naturalWidth > 0 && image.naturalHeight > 0) {
      height = width * (image.naturalHeight / image.naturalWidth)
    }

    let clampedWidth = width
    let clampedHeight = height

    if (shouldLockAsLogo) {
      clampedWidth = Math.min(clampedWidth, sizing.logoLockWidthPx)
      if (clampedWidth > 0 && image.naturalWidth > 0 && image.naturalHeight > 0) {
        clampedHeight = clampedWidth * (image.naturalHeight / image.naturalWidth)
      }
      clampedHeight = Math.min(clampedHeight, sizing.logoLockHeightPx)
    }

    if (clampedWidth <= 0 || clampedHeight <= 0) {
      return
    }

    const widthPx = `${Math.round(clampedWidth)}px`
    const heightPx = `${Math.round(clampedHeight)}px`

    image.setAttribute("width", `${Math.round(clampedWidth)}`)
    image.setAttribute("height", `${Math.round(clampedHeight)}`)
    image.style.width = widthPx
    image.style.height = heightPx
    image.style.maxWidth = widthPx
    image.style.maxHeight = heightPx
    image.style.objectFit = "contain"
  })
}

function applyLogoLockdownStyles(documentRef: Document, sizing: ResolvedPdfSizing) {
  const styleElement = documentRef.createElement("style")
  styleElement.textContent = `
${INVOICE_LOGO_SELECTOR} {
  width: ${sizing.logoLockWidthPx}px !important;
  max-width: ${sizing.logoLockWidthPx}px !important;
  height: auto !important;
  max-height: ${sizing.logoLockHeightPx}px !important;
  object-fit: contain !important;
}
`
  documentRef.head?.appendChild(styleElement)
}

function waitForImages(documentRef: Document) {
  const images = Array.from(documentRef.images)
  const pending = images.filter((image) => !image.complete)

  if (pending.length === 0) {
    return Promise.resolve()
  }

  return Promise.all(
    pending.map(
      (image) =>
        new Promise<void>((resolve) => {
          image.addEventListener("load", () => resolve(), { once: true })
          image.addEventListener("error", () => resolve(), { once: true })
        })
    )
  ).then(() => undefined)
}

function waitForFonts(documentRef: Document) {
  const maybeFonts = (documentRef as Document & { fonts?: FontFaceSet }).fonts
  if (!maybeFonts) {
    return Promise.resolve()
  }

  return maybeFonts.ready.then(() => undefined)
}

function waitForReady(documentRef: Document) {
  return new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      reject(new Error("Invoice took too long to render for PDF generation."))
    }, READY_TIMEOUT_MS)

    Promise.all([waitForImages(documentRef), waitForFonts(documentRef)])
      .then(
        () =>
          new Promise<void>((frameResolve) => {
            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => frameResolve())
            })
          })
      )
      .then(() => {
        window.clearTimeout(timeout)
        resolve()
      })
      .catch((error: unknown) => {
        window.clearTimeout(timeout)
        reject(error)
      })
  })
}

function createHiddenIframe(sizing: ResolvedPdfSizing) {
  const iframe = document.createElement("iframe")
  iframe.setAttribute("aria-hidden", "true")
  iframe.style.position = "fixed"
  iframe.style.left = "-10000px"
  iframe.style.top = "0"
  iframe.style.width = `${sizing.letterWidthPx}px`
  iframe.style.height = "1px"
  iframe.style.opacity = "0"
  iframe.style.pointerEvents = "none"
  document.body.appendChild(iframe)
  return iframe
}

export function sanitizePdfFileName(value: string) {
  const normalized = value
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^[-.]+|[-.]+$/g, "")

  const base = normalized.length > 0 ? normalized : "invoice"
  return base.toLowerCase().endsWith(".pdf") ? base : `${base}.pdf`
}

export async function buildInvoicePdfFromHtml({ html, fileName, pdfSizing }: BuildInvoicePdfArgs) {
  const resolvedSizing = resolvePdfSizing(pdfSizing)
  const iframe = createHiddenIframe(resolvedSizing)

  try {
    const iframeDocument = iframe.contentDocument
    if (!iframeDocument) {
      throw new Error("Could not create an invoice render container.")
    }

    iframeDocument.open()
    iframeDocument.write(html)
    iframeDocument.close()

    applyKnownLogoSourceFallbacks(iframeDocument)
    await waitForReady(iframeDocument)
    stabilizeSvgImageSizing(iframeDocument, resolvedSizing)
    applyLogoLockdownStyles(iframeDocument, resolvedSizing)

    const target = iframeDocument.body
    if (!target) {
      throw new Error("The invoice HTML did not contain a renderable document body.")
    }

    const canvas = await html2canvas(target, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      allowTaint: true,
      windowWidth: Math.max(iframeDocument.documentElement.scrollWidth, resolvedSizing.letterWidthPx),
      windowHeight: Math.max(iframeDocument.documentElement.scrollHeight, target.scrollHeight),
      onclone: (clonedDocument) => {
        applyKnownLogoSourceFallbacks(clonedDocument)
        applyLogoLockdownStyles(clonedDocument, resolvedSizing)
        stabilizeSvgImageSizing(clonedDocument, resolvedSizing)
      },
    })

    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" })
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 24

    const imageData = canvas.toDataURL("image/png")
    const drawWidth = pageWidth - margin * 2
    const drawHeight = (canvas.height * drawWidth) / canvas.width
    const pageUsableHeight = pageHeight - margin * 2

    let heightLeft = drawHeight
    let drawOffset = margin

    pdf.addImage(imageData, "PNG", margin, drawOffset, drawWidth, drawHeight, undefined, "FAST")
    heightLeft -= pageUsableHeight

    while (heightLeft > 0) {
      pdf.addPage("letter", "portrait")
      drawOffset = margin - (drawHeight - heightLeft)
      pdf.addImage(imageData, "PNG", margin, drawOffset, drawWidth, drawHeight, undefined, "FAST")
      heightLeft -= pageUsableHeight
    }

    pdf.save(fileName)
  } finally {
    iframe.remove()
  }
}